import {ONE_HOUR_MS, ONE_MINUTE_MS, ONE_WEEK_MS} from '../utils/time';
import {
  getZoneOffsetMinutes,
  isValidTimeZone,
  wallClockToInstantMs,
} from './zoneOffset';

/**
 * Which clock a course's section times recur on. Absent fields, or
 * `followsCourseTimezone: false`, mean the fixed-UTC-instant recurrence
 * described on {@link computeSectionInstant}.
 */
export interface SectionClock {
  /** The course's IANA zone, from `course.timezone`. */
  timezone?: string;
  /**
   * Whether section times keep their wall-clock time in `timezone` across
   * its offset changes, from `course.sectionTimesFollowCourseTimezone`.
   */
  followsCourseTimezone?: boolean;
}

/**
 * Reads the {@link SectionClock} fields off a course document.
 *
 * @param course - Any object carrying the course's `timezone` and
 *   `sectionTimesFollowCourseTimezone` fields; either may be absent or null.
 * @return The clock to pass to {@link computeSectionInstant} and the helpers
 *   built on it.
 * @throws Never.
 */
export function getSectionClock(course: {
  timezone?: string | null;
  sectionTimesFollowCourseTimezone?: boolean | null;
}): SectionClock {
  return {
    timezone: course.timezone ?? undefined,
    followsCourseTimezone: course.sectionTimesFollowCourseTimezone ?? false,
  };
}

/** Zones already warned about, so a hot loop logs a bad zone once. */
const warnedInvalidZones = new Set<string>();

/**
 * The zone section times follow, or `undefined` for fixed-instant
 * recurrence. Falls back with a warning when the flag is on but the zone is
 * missing or unknown, rather than failing every section surface.
 */
function resolveFollowedZone({
  timezone,
  followsCourseTimezone,
}: SectionClock): string | undefined {
  if (!followsCourseTimezone) {
    return undefined;
  }
  if (isValidTimeZone(timezone)) {
    return timezone;
  }
  const key = String(timezone);
  if (!warnedInvalidZones.has(key)) {
    warnedInvalidZones.add(key);
    console.warn(
      `[slotTime] sectionTimesFollowCourseTimezone is on but course timezone "${key}" is not a valid IANA zone; using fixed UTC instants`,
    );
  }
  return undefined;
}

/**
 * Whether section times follow a valid course zone, i.e. whether
 * {@link computeSectionInstant} uses wall-clock recurrence for this clock.
 *
 * @param clock - The course's {@link SectionClock}.
 * @return `true` only when the flag is on and the zone is valid.
 * @throws Never.
 */
export function isFollowingCourseTimezone(clock: SectionClock): boolean {
  return resolveFollowedZone(clock) !== undefined;
}

/**
 * Canonical UTC instant for a section slot.
 *
 * By default sections are fixed UTC instants: the
 * `(course.firstSectionTimestamp, timeIndex, weekIndex)` triple maps to
 * `anchor + timeIndex hours + weekIndex weeks` in raw milliseconds, so a
 * section's local wall-clock time shifts by the viewer's offset change
 * after a DST transition.
 *
 * When the clock has `followsCourseTimezone` and a valid `timezone`, the
 * same arithmetic runs on the anchor's wall clock in that zone instead:
 * read the anchor's local date-time, add `timeIndex` hours and
 * `weekIndex * 7` calendar days, and convert that wall clock back to an
 * instant. A Tuesday 11:00 section stays Tuesday 11:00 in the course zone
 * every week. A wall clock that happens twice resolves to the earlier
 * instant, and one skipped by a spring-forward moves later by the gap; see
 * {@link wallClockToInstantMs}. With the flag on and the zone missing or
 * invalid, this logs a warning once and returns the fixed instant.
 *
 * This helper is the single source of truth for that mapping across the
 * webapp and Cloud Functions; every other "when does section X happen?"
 * call should delegate here rather than reimplement the arithmetic.
 *
 * Do not use `Date.prototype.setDate`, `setHours`, or any `get*`
 * counterpart in section-time math. Those methods take `process.env.TZ` as
 * an implicit input — browsers run in the user's local timezone, Cloud
 * Functions in UTC — and PR #1975 shipped exactly that client/server
 * mismatch (the Egypt DST regression). Zone math goes through `Intl` with
 * an explicit zone, so output is identical across runtime timezones.
 *
 * @param firstSectionTimestamp - ISO 8601 string from
 *   `course.firstSectionTimestamp`. The course-wide anchor
 *   corresponding to `timeIndex: 0, weekIndex: 0`.
 * @param timeIndex - Hour offset from the anchor. May be negative for
 *   slots scheduled before the baseline section.
 * @param weekIndex - 0-based section-week index. 0 is course week 1.
 *   May be negative.
 * @param timezone - `course.timezone`. Omitted: only fixed-instant
 *   recurrence is possible.
 * @param followsCourseTimezone - `course.sectionTimesFollowCourseTimezone`.
 *   Omitted or false: fixed-instant recurrence, byte-identical to before
 *   the flag existed.
 * @returns The section's start instant.
 * @throws if `firstSectionTimestamp` is not a parseable ISO string
 *   (i.e. `Date.parse` returns `NaN`).
 */
export function computeSectionInstant({
  firstSectionTimestamp,
  timeIndex,
  weekIndex,
  timezone,
  followsCourseTimezone,
}: {
  firstSectionTimestamp: string;
  timeIndex: number;
  weekIndex: number;
} & SectionClock): Date {
  const anchorMs = Date.parse(firstSectionTimestamp);
  if (Number.isNaN(anchorMs)) {
    throw new Error(
      `computeSectionInstant: unparseable firstSectionTimestamp "${firstSectionTimestamp}"`,
    );
  }
  const offsetMs = timeIndex * ONE_HOUR_MS + weekIndex * ONE_WEEK_MS;
  const zone = resolveFollowedZone({timezone, followsCourseTimezone});
  if (zone === undefined) {
    return new Date(anchorMs + offsetMs);
  }
  // Seven calendar days of wall clock is always ONE_WEEK_MS, so the
  // arithmetic stays in raw ms; only the two zone conversions are local.
  const anchorWallClockMs =
    anchorMs +
    getZoneOffsetMinutes({instantMs: anchorMs, timeZone: zone}) * ONE_MINUTE_MS;
  return new Date(
    wallClockToInstantMs({
      wallClockMs: anchorWallClockMs + offsetMs,
      timeZone: zone,
    }),
  );
}

/**
 * Finds the next upcoming section instant in the sequence defined by
 * `(firstSectionTimestamp, timeIndex)`. A section remains "current"
 * (not yet replaced by the next week's slot) for `graceMs` after its
 * start instant — defaulting to one hour, matching the student
 * dashboard's pre-existing grace window.
 *
 * Pure and deterministic: `nowMs` is an explicit parameter so callers
 * thread in either server time or a test fixture clock. No internal
 * `Date.now()`, no ambient-tz reads. Output instant is byte-identical
 * across runtime timezones; each week's instant comes from
 * {@link computeSectionInstant} under `clock`.
 *
 * @param firstSectionTimestamp - ISO 8601 course anchor (timeIndex 0,
 *   weekIndex 0).
 * @param timeIndex - Hour offset from the anchor.
 * @param nowMs - Current epoch-ms. Caller-provided.
 * @param graceMs - Window during which the just-started section is
 *   still considered "next". Defaults to `ONE_HOUR_MS`. Pass `0` to
 *   advance to the next week at the exact section start instant.
 * @param clock - The course's {@link SectionClock}. Omitted: fixed-instant
 *   recurrence.
 * @returns The upcoming section instant and its 0-based section-week
 *   index. `sectionWeekIdx + 1` is the 1-based "course week" the UI
 *   typically shows to users.
 * @throws if `firstSectionTimestamp` is unparseable (propagated from
 *   {@link computeSectionInstant}).
 */
export function computeNextSectionSlot({
  firstSectionTimestamp,
  timeIndex,
  nowMs,
  graceMs = ONE_HOUR_MS,
  clock = {},
}: {
  firstSectionTimestamp: string;
  timeIndex: number;
  nowMs: number;
  graceMs?: number;
  clock?: SectionClock;
}): {nextSectionDate: Date; sectionWeekIdx: number} {
  return computeNextWeeklyInstant({
    meetingForWeek: weekIndex =>
      computeSectionInstant({
        firstSectionTimestamp,
        timeIndex,
        weekIndex,
        ...clock,
      }),
    nowMs,
    graceMs,
  });
}

/** Applies weekly recurrence and the section-start grace period. */
function computeNextWeeklyInstant({
  meetingForWeek,
  nowMs,
  graceMs,
}: {
  meetingForWeek: (weekIndex: number) => Date;
  nowMs: number;
  graceMs: number;
}): {nextSectionDate: Date; sectionWeekIdx: number} {
  let sectionWeekIdx = 0;
  let sectionDate = meetingForWeek(0);
  while (nowMs >= sectionDate.getTime() + graceMs) {
    sectionWeekIdx += 1;
    sectionDate = meetingForWeek(sectionWeekIdx);
  }
  return {nextSectionDate: sectionDate, sectionWeekIdx};
}

/**
 * Returns the 0-based course section week containing a Super section's first
 * meeting. A missing or invalid course anchor treats that meeting as week 1.
 */
export function getSuperSectionCourseWeekIndex({
  superSectionFirstTimestamp,
  courseFirstSectionTimestamp,
}: {
  superSectionFirstTimestamp: string;
  courseFirstSectionTimestamp?: string;
}): number {
  const superAnchorMs = Date.parse(superSectionFirstTimestamp);
  if (!Number.isFinite(superAnchorMs)) {
    throw new Error(
      `getSuperSectionCourseWeekIndex: unparseable Super section timestamp "${superSectionFirstTimestamp}"`,
    );
  }

  const courseAnchorMs = courseFirstSectionTimestamp
    ? Date.parse(courseFirstSectionTimestamp)
    : Number.NaN;
  if (!Number.isFinite(courseAnchorMs)) return 0;

  return Math.max(
    0,
    Math.floor((superAnchorMs - courseAnchorMs) / ONE_WEEK_MS),
  );
}

/**
 * Finds the next meeting for a Super section and maps it to the course week
 * used by the section page and RSVP documents.
 *
 * @param superSectionFirstTimestamp - ISO 8601 timestamp of the first Super
 *   meeting. Its offset is part of the scheduled instant.
 * @param courseFirstSectionTimestamp - Optional course section anchor used to
 *   align the Super meeting with the course's section-week numbering. If it is
 *   omitted or invalid, the Super section's first meeting is treated as week 1.
 * @param nowMs - Current epoch milliseconds supplied by the caller.
 * @return The upcoming meeting and its zero-based course week index. Add 1 to
 *   `courseWeekIdx` to get the one-based RSVP document key.
 * @throws If `superSectionFirstTimestamp` is not a parseable ISO timestamp.
 */
export function computeNextSuperSectionSlot({
  superSectionFirstTimestamp,
  courseFirstSectionTimestamp,
  nowMs,
}: {
  superSectionFirstTimestamp: string;
  courseFirstSectionTimestamp?: string;
  nowMs: number;
}): {nextSectionDate: Date; courseWeekIdx: number} {
  const firstMeetingCourseWeekIdx = getSuperSectionCourseWeekIndex({
    superSectionFirstTimestamp,
    courseFirstSectionTimestamp,
  });
  const firstMeetingMs = new Date(superSectionFirstTimestamp).getTime();
  const {nextSectionDate, sectionWeekIdx} = computeNextWeeklyInstant({
    meetingForWeek: weekIndex =>
      new Date(firstMeetingMs + weekIndex * ONE_WEEK_MS),
    nowMs,
    graceMs: ONE_HOUR_MS,
  });

  return {
    nextSectionDate,
    courseWeekIdx: firstMeetingCourseWeekIdx + sectionWeekIdx,
  };
}
